import { useEffect, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import "./WeighbridgeCaptureModal.scss";

/**
 * Dialog shown while a vehicle capture runs. It surfaces BOTH tracks at once —
 * the live weight (shown on a seven-segment "LCD" while it settles) and the
 * vehicle photo — so the per-sale wait is informative and pleasant.
 *
 * Props:
 *   show   - whether a capture is in progress
 *   weight - { enabled, status: 'active'|'done'|'failed', value, info } (info = live captureInfo)
 *   image  - { enabled, status: 'active'|'done'|'failed', url }
 */
export function WeighbridgeCaptureModal({ show, weight = {}, image = {} }) {
    // Re-render a few times a second so the live reading and progress bar animate
    // smoothly between weighbridge messages.
    const [, setTick] = useState(0);
    useEffect(() => {
        if (!show) return undefined;
        const id = setInterval(() => setTick((t) => t + 1), 150);
        return () => clearInterval(id);
    }, [show]);

    const tracks = [weight, image].filter((t) => t && t.enabled);
    const allDone = tracks.length > 0 && tracks.every((t) => t.status === "done");
    const anyActive = tracks.some((t) => t.status === "active");

    return (
        <Modal show={show} centered backdrop="static" keyboard={false} dialogClassName="wb-capture-modal">
            <div className="wb-capture-head">
                <div className="wb-capture-head__title">{allDone ? "All set!" : "Capturing…"}</div>
                <div className="wb-capture-head__sub">
                    {allDone
                        ? "Saving these to your entry"
                        : anyActive
                        ? "Please hold on for a moment"
                        : "Getting ready…"}
                </div>
            </div>

            <div className="wb-capture-body">
                {weight.enabled && <WeightTrack weight={weight} />}
                {image.enabled && <ImageTrack image={image} />}

                <div className="wb-capture-foot">
                    {allDone
                        ? "Done — closing automatically"
                        : "We wait for a steady reading so the weight is accurate"}
                </div>
            </div>
        </Modal>
    );
}

const trackClass = (status) =>
    `wb-track ${
        status === "done" ? "is-done" : status === "failed" ? "is-failed" : "is-active"
    }`;

// Format any reading to a fixed 3-decimal string for a stable seven-segment look.
const lcd = (v) => (typeof v === "number" && !isNaN(v) ? v.toFixed(3) : null);

function WeightTrack({ weight }) {
    const { status, value, info = {} } = weight;
    const phase = info.phase || "connecting";
    const isSteady = (info.readings || 0) >= 2;
    const maxMs = info.maxMs || 7000;
    const elapsed = info.startedAt ? Date.now() - info.startedAt : 0;
    const pct = status === "done" ? 100 : Math.min(100, Math.round((elapsed / maxMs) * 100));

    let statusText = "Connecting to weighbridge…";
    if (status === "done") statusText = "Captured";
    else if (status === "failed") statusText = "Couldn't read a steady weight";
    else if (phase === "waiting") statusText = "Waiting for a reading…";
    else if (phase === "stabilizing") statusText = isSteady ? "Holding steady…" : "Still settling…";

    const digits = lcd(status === "done" ? value : info.latest);
    const settling = status === "active" && !isSteady;

    return (
        <div className={trackClass(status)}>
            <div className="wb-track__row">
                <span className="wb-track__icon">
                    {status === "done" ? (
                        <i className="bi bi-check-lg" />
                    ) : status === "failed" ? (
                        <i className="bi bi-exclamation-lg" />
                    ) : (
                        <i className="bi bi-speedometer2" />
                    )}
                </span>
                <div className="wb-track__main">
                    <div className="wb-track__label">Weight</div>
                    <div className="wb-track__status">{statusText}</div>
                </div>
                {digits != null ? (
                    <span className={`wb-lcd ${settling ? "is-settling" : ""}`}>
                        <span className="wb-lcd__digits">{digits}</span>
                        <span className="wb-lcd__unit">t</span>
                    </span>
                ) : status === "active" ? (
                    <Spinner animation="border" size="sm" variant="primary" />
                ) : null}
            </div>
            {status === "active" && phase === "stabilizing" && (
                <div className="wb-track__bar">
                    <span className={isSteady ? "is-steady" : ""} style={{ width: `${pct}%` }} />
                </div>
            )}
        </div>
    );
}

function ImageTrack({ image }) {
    const { status, url } = image;
    let statusText = "Taking photo…";
    if (status === "done") statusText = "Captured";
    else if (status === "failed") statusText = "Camera not available";

    return (
        <div className={trackClass(status)}>
            <div className="wb-track__row">
                <span className="wb-track__icon">
                    {status === "done" ? (
                        <i className="bi bi-check-lg" />
                    ) : status === "failed" ? (
                        <i className="bi bi-camera-video-off" />
                    ) : (
                        <i className="bi bi-camera" />
                    )}
                </span>
                <div className="wb-track__main">
                    <div className="wb-track__label">Vehicle Photo</div>
                    <div className="wb-track__status">{statusText}</div>
                </div>
                {status === "done" && url ? (
                    <img className="wb-track__thumb" src={url} alt="Captured vehicle" />
                ) : status === "active" ? (
                    <Spinner animation="border" size="sm" variant="primary" />
                ) : null}
            </div>
        </div>
    );
}
